import type { MaybeOptionalInit, ParseAs } from "openapi-fetch";
import type { PathsWithMethod } from "openapi-typescript-helpers";

import { createApiClient } from "./client.ts";
import type { paths } from "./openapi.ts";
import { throwErrors } from "./throw-errors.ts";
import { urls } from "./urls.ts";

type GetOptions<Path extends PathsWithMethod<paths, "get">> = Exclude<MaybeOptionalInit<paths[Path], "get">, undefined>;
type GetPathParams<Path extends PathsWithMethod<paths, "get">> = Exclude<GetOptions<Path>["params"], undefined>["path"];
type GetQueryParams<Path extends PathsWithMethod<paths, "get">> = Exclude<
  GetOptions<Path>["params"],
  undefined
>["query"];

type DeleteOptions<Path extends PathsWithMethod<paths, "delete">> = Exclude<
  MaybeOptionalInit<paths[Path], "delete">,
  undefined
>;
type DeletePathParams<Path extends PathsWithMethod<paths, "delete">> = Exclude<
  DeleteOptions<Path>["params"],
  undefined
>["path"];
type DeleteQueryParams<Path extends PathsWithMethod<paths, "delete">> = Exclude<
  DeleteOptions<Path>["params"],
  undefined
>["query"];

type PostOptions<Path extends PathsWithMethod<paths, "post">> = Exclude<
  MaybeOptionalInit<paths[Path], "post">,
  undefined
>;
type PostPathParams<Path extends PathsWithMethod<paths, "post">> = Exclude<
  PostOptions<Path>["params"],
  undefined
>["path"];
type PostQueryParams<Path extends PathsWithMethod<paths, "post">> = Exclude<
  PostOptions<Path>["params"],
  undefined
>["query"];
type PostBody<Path extends PathsWithMethod<paths, "post">> = PostOptions<Path>["body"];

type PutOptions<Path extends PathsWithMethod<paths, "put">> = Exclude<MaybeOptionalInit<paths[Path], "put">, undefined>;
type PutPathParams<Path extends PathsWithMethod<paths, "put">> = Exclude<PutOptions<Path>["params"], undefined>["path"];
type PutQueryParams<Path extends PathsWithMethod<paths, "put">> = Exclude<
  PutOptions<Path>["params"],
  undefined
>["query"];
type PutBody<Path extends PathsWithMethod<paths, "put">> = PutOptions<Path>["body"];

const DEFAULT_CACHE = "no-cache" as RequestCache;

function withDefaults<
  Method extends "get" | "post" | "put" | "delete",
  Path extends PathsWithMethod<paths, Method>,
  Options extends Exclude<MaybeOptionalInit<paths[Path], Method>, undefined>,
>(options: Options | undefined) {
  return {
    cache: DEFAULT_CACHE,
    ...(options ?? {}),
  } as MaybeOptionalInit<paths[Path], Method> & Record<string, unknown>;
}

const client = createApiClient(urls);

export async function get<Path extends PathsWithMethod<paths, "get">>(
  path: Path,
  pathParams: GetPathParams<Path>,
  queryParams?: GetQueryParams<Path>,
  parseAs: ParseAs = "json",
  signal?: AbortSignal,
  options?: Omit<GetOptions<Path>, "params" | "body">, // body is not supported by the sdk and is unusual
) {
  const localOptions = { ...options, params: {} } as GetOptions<Path>;
  const { params } = localOptions;

  params!.path = pathParams;
  params!.query = queryParams;

  localOptions.parseAs = parseAs;
  localOptions.signal = signal;

  const result = await client.GET(path, withDefaults<"get", Path, GetOptions<Path>>(localOptions));
  const { data, error, response } = result;
  throwErrors(response, data, error);
  return data;
}

export async function getWithHeaders<Path extends PathsWithMethod<paths, "get">>(
  path: Path,
  pathParams: GetPathParams<Path>,
  queryParams?: GetQueryParams<Path>,
  parseAs: ParseAs = "json",
  signal?: AbortSignal,
  options?: Omit<GetOptions<Path>, "params" | "body">, // body is not supported by the sdk and is unusual
) {
  const localOptions = { ...options, params: {} } as GetOptions<Path>;
  const { params } = localOptions;

  params!.path = pathParams;
  params!.query = queryParams;

  localOptions.parseAs = parseAs;
  localOptions.signal = signal;

  const result = await client.GET(path, withDefaults<"get", Path, GetOptions<Path>>(localOptions));
  const { data, error, response } = result;
  throwErrors(response, data, error);
  return { data, headers: response.headers };
}

export async function post<Path extends PathsWithMethod<paths, "post">>(
  path: Path,
  pathParams: PostPathParams<Path> | undefined,
  queryParams: PostQueryParams<Path> | undefined,
  postBody: PostBody<Path>,
  parseAs: ParseAs = "json",
  signal?: AbortSignal,
  options?: Omit<PostOptions<Path>, "params" | "body" | "parseAs" | "signal">,
) {
  const localOptions = { ...options, params: {} } as PostOptions<Path>;
  const { params } = localOptions;

  params!.path = pathParams;
  params!.query = queryParams;

  localOptions.body = postBody;
  localOptions.parseAs = parseAs;
  localOptions.signal = signal;

  const result = await client.POST(path, withDefaults<"post", Path, PostOptions<Path>>(localOptions));
  const { data, error, response } = result;
  throwErrors(response, data, error);
  return { data, response, error };
}

export async function put<Path extends PathsWithMethod<paths, "put">>(
  path: Path,
  pathParams: PutPathParams<Path> | undefined,
  queryParams: PutQueryParams<Path> | undefined,
  putBody: PutBody<Path>,
  parseAs: ParseAs = "json",
  signal?: AbortSignal,
  options?: PutOptions<Path>,
) {
  const localOptions = { ...options, params: {} } as PutOptions<Path>;
  const { params } = localOptions;

  params!.path = pathParams;
  params!.query = queryParams;

  localOptions.body = putBody;
  localOptions.parseAs = parseAs;
  localOptions.signal = signal;

  const result = await client.PUT(path, withDefaults<"put", Path, PutOptions<Path>>(localOptions));
  const { data, error, response } = result;
  throwErrors(response, data, error);
  return data;
}

export async function del<Path extends PathsWithMethod<paths, "delete">>(
  path: Path,
  pathParams: DeletePathParams<Path>,
  queryParams?: DeleteQueryParams<Path>,
  options?: Omit<DeleteOptions<Path>, "params" | "body">, // body is not supported by the sdk and is unusual
) {
  const localOptions = { ...options, params: {} } as DeleteOptions<Path>;
  const { params } = localOptions;

  params.path = pathParams;
  params.query = queryParams;

  const result = await client.DELETE(path, withDefaults<"delete", Path, DeleteOptions<Path>>(localOptions));
  const { data, error, response } = result;
  throwErrors(response, data, error);
  return data;
}
